<template>
    <div v-if="parsedEmailMessage" class="CriterionEmailMessage text-[0.8rem] mt-10 py-2.5 px-[15px]">
        <div>
            Email Message
        </div>
        <div class="text-eidLightGray text-[0.8rem] my-[15px]">
            If Non-compliant, the Entity will receive the following message:
        </div>
        <div class="p-2.5" data-test-id="CriterionEmailMessage__emailMessageContent">
            {{ parsedEmailMessage }}
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import get from 'lodash/get';
    import mapValues from 'lodash/mapValues';

    import { createRpController } from '@evidentid/dashboard-commons/modules/auth/createRpController';
    import JsonSchema, { JsonSchemaArray, JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { isArray, isEnum } from '@evidentid/json-schema/schemaChecks';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';

    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { CriterionMessageStatus } from '@/modules/decisioning-criteria/store';

    export default defineComponent({
        name: 'CriterionEmailMessage',
        props: {
            criterion: {
                type: Object as PropType<EditableRequirementCriterion>,
                required: true,
            },
            template: {
                type: Object as PropType<CriterionTemplate>,
                required: true,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [] as CustomProperty[],
            },
            criterionValue: {
                type: Object as PropType<any | null>,
                default: () => null,
            },
            criterionMessageStatus: {
                type: Object as PropType<CriterionMessageStatus>,
                required: true,
            },
        },
        computed: {
            rpName(): string {
                return this.$route.params.rpId as string;
            },
            parsedEmailMessage(): string {
                const message = this.criterionMessageStatus.data?.nonComplianceMessage || this.template.emailMessage;
                const rpPlaceholderRegex = /\{\{RELYING_PARTY\:.*?\}\}/g;
                const referencePlaceholderRegex = /\{\{REFERENCE\:.*?\}\}/g;
                const customPropertyPlaceholderRegex = /\{\{INSURED_FIELD\:.*?\}\}/g;
                const referencePlaceholders = message.match(referencePlaceholderRegex);
                const rpPropertiesPlaceholders = message.match(rpPlaceholderRegex);
                const customPropertyPlaceholders = message.match(customPropertyPlaceholderRegex);

                let messageCopy = message;
                if (referencePlaceholders) {
                    messageCopy = this.replaceReferencesInMessage(messageCopy, referencePlaceholders as string[]);
                }
                if (rpPropertiesPlaceholders) {
                    messageCopy = this.replaceRpPropertyInMessage(messageCopy, rpPropertiesPlaceholders as string[]);
                }
                if (customPropertyPlaceholders) {
                    messageCopy =
                        this.replaceCustomPropertyInMessage(messageCopy, customPropertyPlaceholders as string[]);
                }
                return messageCopy;
            },
        },
        methods: {
            replaceRpPropertyInMessage(message: string, rpPlaceholders: string[]): string {
                const propKeyRegex = /(?<=\{\{RELYING_PARTY:)(.*?)(?=\}\})/;
                let messageCopy = message;
                // TODO(PRODUCT-18675): remove any after the migration to the newer version.
                const $rp = (this as any).$rp as ReturnType<typeof createRpController>;
                const rp = $rp.available.find((rp) => rp.name === $rp.current) as any;
                rpPlaceholders.forEach((placeholder) => {
                    const match = placeholder.match(propKeyRegex);
                    const propKey = match ? match[0] : null;
                    if (propKey && rp[propKey]) {
                        messageCopy = messageCopy.replaceAll(
                            placeholder,
                            this.getStringValue(rp[propKey]),
                        );
                    }
                });
                return messageCopy;
            },
            replaceReferencesInMessage(message: string, referencePlaceholders: string[]): string {
                // keep checking for insured field as a fallback in case the email message is being used
                const referenceKeyRegex = /(?<=\{\{REFERENCE:)(.*?)(?=\}\})/;
                return this.replaceRefValue(message, referencePlaceholders, referenceKeyRegex);
            },
            replaceCustomPropertyInMessage(message: string, referencePlaceholders: string[]): string {
                const insuredFieldKeyRegex = /(?<=\{\{INSURED_FIELD:)(.*?)(?=\}\})/;
                return this.replaceCustomPropertyName(message, referencePlaceholders, insuredFieldKeyRegex);
            },
            replaceRefValue(message: string, stringsTobeReplaced: string[], keyRegex: RegExp) {
                let messageCopy = message;
                stringsTobeReplaced.forEach((placeholder) => {
                    const match = placeholder.match(keyRegex);
                    const refKey = match ? match[0] : null;
                    if (refKey) {
                        const value = get(this.criterionValue, refKey);
                        const valueOrLabel = this.getPossibleLabel(value, refKey) || value;
                        messageCopy = messageCopy.replaceAll(
                            placeholder,
                            this.getStringValue(valueOrLabel),
                        );
                    }
                });
                return messageCopy;
            },
            replaceCustomPropertyName(message: string, stringsTobeReplaced: string[], keyRegex: RegExp) {
                let messageCopy = message;
                stringsTobeReplaced.forEach((placeholder) => {
                    const match = placeholder.match(keyRegex);
                    const insuredFieldKey = match ? match[0] : null;
                    if (insuredFieldKey) {
                        const insuredField = this.customProperties.find((field) => field.key === insuredFieldKey);
                        messageCopy = messageCopy.replaceAll(placeholder, insuredField?.name || '');
                    }
                });
                return messageCopy;
            },
            getPossibleLabel(value: any, reference: string): string | null {
                const schema = this.getReferenceSchema(reference);
                // FIXME once BE model move all enum labels into Schema, getLabelWithinTemplate no longer needed.
                return this.getLabelWithinSchema(value, schema)
                    || this.getLabelWithinBooleanSchema(value, schema)
                    || this.getLabelWithinTemplate(value, reference);
            },
            /** Resolves a reference's schema, looking into the branches of an if/then/else as well as the
             * schema's own properties.
             *
             * A ValueOrInsuredField-wrapped reference keeps its value schema under `else.properties.value`
             * rather than `properties.value`, so a `<name>.value` token misses on the root and the value is
             * rendered raw (PRODUCT-18727). The direct path is tried first, so unwrapped references resolve
             * exactly as before.
             */
            getReferenceSchema(reference: string): JsonSchema | null {
                const referenceSchemas = mapValues(this.template.evaluator.references, (ref) => ref.schema);
                const [ root, ...rest ] = this.convertJsonPathToSchemaPath(reference).split('.');
                const candidates = rest.length === 0
                    ? [ [ root ] ]
                    : [ [ root, ...rest ], [ root, 'else', ...rest ], [ root, 'then', ...rest ] ];
                for (const candidate of candidates) {
                    const found = get(referenceSchemas, candidate.join('.'));
                    if (found) {
                        return found as JsonSchema;
                    }
                }
                return null;
            },
            /** Boolean criterion values read as Yes/No throughout the product -- CriterionBooleanInput
             * renders them with those labels, and bulk import validates the column as "Must contain
             * Yes/No". The criterion's own enumValues cannot supply them: they are keyed on the bare
             * reference name and on the "Yes"/"No" strings, while the token carries a `.value` suffix and
             * the stored value is a boolean.
             */
            getLabelWithinBooleanSchema(value: any, schema: JsonSchema | null): string | null {
                if (!schema || (schema as any).type !== 'boolean' || typeof value !== 'boolean') {
                    return null;
                }
                return value ? 'Yes' : 'No';
            },
            convertJsonPathToSchemaPath(path: string): string {
                const regexForProperties = /\.(\w+)/;
                const regexForItems = /\[\d+\]/;
                let schemaPath = path.replace(regexForProperties, '.properties.$1');
                schemaPath = schemaPath.replace(regexForItems, '.items');
                return schemaPath;
            },
            getLabelWithinTemplate(value: any, reference: string): string | null {
                if (!this.template.displayMetadata.enumValues) {
                    return null;
                }
                // enumValues are keyed on the bare reference name, but a ValueOrInsuredField-wrapped
                // reference's token carries a `.value` suffix, so the exact match misses. Falling back to
                // the root name lets a wrapped reference that kept an enum value schema find its labels
                // (PRODUCT-18727).
                //
                // This cannot help a wrapped Yes/No reference: its keys are the "Yes"/"No" strings while
                // the stored value is a boolean, and no keying of this map expresses that true means Yes.
                // getLabelWithinBooleanSchema covers that case from the schema instead.
                const rootReference = reference.split('.')[0];
                const valueLabelMap = this.template.displayMetadata.enumValues?.find(
                    (maps: { reference: string, values: Record<string, string> }) =>
                        maps.reference === reference || maps.reference === rootReference,
                )?.values || {};
                const arrayValue = Array.isArray(value) ? value : [ value ];
                return arrayValue.map((val) => valueLabelMap[val] || null).filter(Boolean).join(', ') || null;
            },
            getLabelWithinSchema(value: any, schema: JsonSchema | null): string | null {
                // only enum schema needs to get label, thus only consider enum and array of enum types
                const isEnumSchema = schema && isEnum(schema);
                const isMultiEnumSchema = schema && isArray(schema) && isEnum(schema.items as JsonSchema);
                if (!isEnumSchema && !isMultiEnumSchema) {
                    return null;
                }
                const enumSchema = ((schema as JsonSchemaArray).items || schema) as JsonSchemaEnum;
                const arrayValue = Array.isArray(value) ? value : [ value ];
                return arrayValue.map((val) => {
                    const index = enumSchema.enum.indexOf(val);
                    return enumSchema.enumLabels?.[index] || null;
                }).filter(Boolean).join(', ') || null;
            },
            getStringValue(value: any): string {
                // object is not going to be expected per current requirement,
                // if it shows up, the view will render [Object Object] as an indication we need to add support for it
                if (value == null) {
                    return '';
                }
                return Array.isArray(value) ? value.join(', ') : String(value);
            },
        },
    });
</script>

